import { useCallback, useEffect, useRef, useState } from 'react';
import { Icons } from '../components/Icons';
import { apiRequest, getApiBaseUrl } from '../lib/api';
import { getPublicBaseUrl } from '../lib/site';
import { nameToSlug } from '../utils/validation';

function mapReferralData(user) {
  return {
    code: user.referralCode,
    points: user.referralPoints || 0,
    totalShares: user.referralHistory?.length || 0,
    signups: 0,
    history: (user.referralHistory || []).map((item) => ({
      id: item.id,
      platform: item.platform,
      time: new Date(item.createdAt).toLocaleString(),
      points: item.points
    }))
  };
}

export default function ReferralsPage({ user }) {
  const [referralData, setReferralData] = useState(() => mapReferralData(user));
  const [copied, setCopied] = useState(false);
  const [generatingIg, setGeneratingIg] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    setReferralData(mapReferralData(user));
  }, [user]);

  const referralLink = `${getPublicBaseUrl()}/join?ref=${referralData.code}`;
  const invitationMessage = `Hey! ${user.name} is inviting you to join Connekted.in - the free digital business card platform that lets you create a stunning QR-powered profile in 90 seconds.\n\nNo more paper cards. Just scan, connect, and grow your network.\n\nSign up here: ${referralLink}`;

  const recordShare = useCallback((platform) => {
    apiRequest('/users/me/referrals/share', { method: 'POST', body: { platform } }).catch(() => {});
    setReferralData((prev) => ({
      ...prev,
      points: prev.points + 10,
      totalShares: prev.totalShares + 1,
      history: [{ id: `local-${Date.now()}`, platform, time: 'Just now', points: 10 }, ...prev.history]
    }));
  }, []);

  const handleShare = (platform) => {
    recordShare(platform);
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(invitationMessage)}`,
      email: `mailto:?subject=${encodeURIComponent(`${user.name} invited you to Connekted.in`)}&body=${encodeURIComponent(invitationMessage)}`
    };
    const url = urls[platform];
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyInvitation = () => {
    navigator.clipboard.writeText(invitationMessage).then(() => {
      recordShare('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleInstagramShare = useCallback(() => {
    setGeneratingIg(true);
    recordShare('instagram');

    const token = localStorage.getItem('connektedin_token');
    fetch(`${getApiBaseUrl()}/users/me/qr`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then((response) => response.blob())
      .then((blob) => createImageBitmap(blob))
      .then((qrImage) => {
        const canvas = canvasRef.current || document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 1080;
        canvas.width = size;
        canvas.height = size;

        ctx.fillStyle = '#0A2540';
        ctx.fillRect(0, 0, size, size);

        const grad = ctx.createLinearGradient(0, 0, size, size);
        grad.addColorStop(0, 'rgba(201, 169, 97, 0.10)');
        grad.addColorStop(1, 'rgba(201, 169, 97, 0.02)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, size, size);

        ctx.fillStyle = '#F5F1E8';
        ctx.font = '600 36px "DM Sans", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Scan to connect with', size / 2, 136);

        ctx.fillStyle = '#C9A961';
        ctx.font = '300 60px "Fraunces", Georgia, serif';
        ctx.fillText(user.name, size / 2, 214);

        ctx.fillStyle = 'rgba(245, 241, 232, 0.74)';
        ctx.font = '400 24px "DM Sans", Arial, sans-serif';
        const subtitle = [user.title, user.company].filter(Boolean).join(' at ');
        if (subtitle) {
          ctx.fillText(subtitle, size / 2, 262);
        }

        const qrSize = 520;
        const qrX = (size - qrSize) / 2;
        const qrY = 320;
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.roundRect(qrX - 18, qrY - 18, qrSize + 36, qrSize + 36, 24);
        ctx.fill();
        ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

        ctx.fillStyle = 'rgba(245, 241, 232, 0.75)';
        ctx.font = '400 20px "DM Sans", Arial, sans-serif';
        ctx.fillText('Free digital business cards for professionals', size / 2, qrY + qrSize + 74);

        ctx.fillStyle = '#C9A961';
        ctx.font = '500 22px "JetBrains Mono", monospace';
        ctx.fillText(referralLink, size / 2, qrY + qrSize + 114);

        canvas.toBlob((result) => {
          const downloadUrl = URL.createObjectURL(result);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `connektedin-invite-${nameToSlug(user.name)}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);
          setGeneratingIg(false);
        }, 'image/png');
      })
      .catch(() => {
        setGeneratingIg(false);
      });
  }, [recordShare, referralLink, user]);

  const tiers = [
    { name: 'Starter', min: 0, icon: 'S' },
    { name: 'Connector', min: 100, icon: 'C' },
    { name: 'Ambassador', min: 500, icon: 'A' },
    { name: 'Pioneer', min: 1000, icon: 'P' }
  ];
  const currentTier = [...tiers].reverse().find((tier) => referralData.points >= tier.min) || tiers[0];
  const nextTier = tiers[tiers.indexOf(currentTier) + 1];
  const progress = nextTier
    ? ((referralData.points - currentTier.min) / (nextTier.min - currentTier.min)) * 100
    : 100;

  return (
    <>
      <div className="page-header">
        <div className="mono" style={{ marginBottom: 8 }}>Referrals</div>
        <h1 className="page-title">
          Grow the <em>network</em>.
        </h1>
        <p className="page-subtitle">Share Connekted.in, earn points, unlock early access to future features.</p>
      </div>

      <div className="page-body">
        <div className="panel panel-dark animate-fadeUp">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div className="mono" style={{ marginBottom: 4 }}>Your Points</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 48, fontWeight: 300, color: 'var(--gold)' }}>
                {referralData.points}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="mono" style={{ marginBottom: 4 }}>Current Tier</div>
              <div style={{ fontSize: 20, fontWeight: 500, color: 'var(--paper)' }}>
                {currentTier.icon} {currentTier.name}
              </div>
            </div>
          </div>

          {nextTier && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(245,242,235,0.7)', marginBottom: 6 }}>
                <span>{currentTier.name}</span>
                <span>{nextTier.name} - {nextTier.min} pts</span>
              </div>
              <div style={{ height: 6, background: 'rgba(245,242,235,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(progress, 100)}%`, background: 'var(--gold)', borderRadius: 3, transition: 'width 0.6s ease' }} />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 24, marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(245,242,235,0.08)' }}>
            <div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: 'var(--paper)' }}>{referralData.totalShares}</div>
              <div className="mono" style={{ fontSize: 9 }}>Total Shares</div>
            </div>
            <div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: 'var(--paper)' }}>{referralData.signups}</div>
              <div className="mono" style={{ fontSize: 9 }}>Sign-ups</div>
            </div>
            <div>
              <div style={{ fontFamily: "'Fraunces', serif", fontSize: 24, color: 'var(--paper)' }}>10</div>
              <div className="mono" style={{ fontSize: 9 }}>Pts per Share</div>
            </div>
          </div>
        </div>

        <div className="panel animate-fadeUp" style={{ animationDelay: '0.1s' }}>
          <div className="panel-label">Your Invitation</div>
          <div
            style={{
              background: 'var(--paper-deep)',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r-sm)',
              padding: '16px 20px',
              marginBottom: 16,
              whiteSpace: 'pre-line',
              fontSize: 14,
              lineHeight: 1.7,
              color: 'var(--ink)'
            }}
          >
            {invitationMessage}
          </div>

          <button className="btn btn-primary-app" onClick={handleCopyInvitation} style={{ marginBottom: 16 }}>
            {copied ? <>{Icons.check} Copied!</> : <>Copy Invitation</>}
          </button>

          <div className="panel-label" style={{ marginTop: 8 }}>Share via</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-whatsapp btn-sm" onClick={() => handleShare('whatsapp')}>
              {Icons.whatsapp} WhatsApp
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => handleShare('email')}>
              {Icons.mail} Email Invite
            </button>
            <button
              className="btn btn-sm"
              onClick={handleInstagramShare}
              disabled={generatingIg}
              style={{
                background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                color: 'white',
                border: 'none'
              }}
            >
              {Icons.instagram} {generatingIg ? 'Generating...' : 'Share on Instagram'}
            </button>
          </div>

          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, lineHeight: 1.5 }}>
            Instagram: downloads a branded share image with your live QR code instead of a placeholder mockup.
          </p>
        </div>

        <div className="panel animate-fadeUp" style={{ animationDelay: '0.15s' }}>
          <div className="panel-label">How Referrals Work</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {[
              ['1', 'Share', 'Share your invitation on any platform'],
              ['2', 'Earn Points', 'Get 10 points for every share, 50 for every sign-up'],
              ['3', 'Unlock Early Access', 'Higher points means earlier access to future features']
            ].map(([step, title, text]) => (
              <div key={step} style={{ textAlign: 'center', padding: 16 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{step}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{text}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="panel animate-fadeUp" style={{ animationDelay: '0.2s' }}>
          <div className="panel-label">Recent Activity</div>
          {referralData.history.map((entry) => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid var(--rule)'
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Shared via {entry.platform}</div>
                <div className="mono" style={{ marginTop: 2 }}>{entry.time}</div>
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: 'var(--success)', fontWeight: 600 }}>
                +{entry.points} pts
              </div>
            </div>
          ))}
        </div>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );
}
